/**
 * VIEW PILOT RECORD, and the VIEW TOP ACES list beside it.
 *
 * The record's fields are `PS.EXE`'s own, in its own order: `Status:` (one of
 * Active / Retired / Discharged / Deceased), `SCORE`, `SQUADRON NAME`,
 * `AERODROME LOCATION`, `PLANE TYPE`, `DATE`, and the victory counts
 * `AIRCRAFT` / `BALLOONS` / `ZEPPELINS` / `TOTAL VICTORIES` / `MEDALS`
 * (docs/game/campaign.md § *Wounds, death, and discharge*). The manual adds
 * that the score shown is "the accumulation of all mission scores, and all
 * medals that have been received", which is what `careerScore` holds.
 *
 * VIEW TOP ACES is the manual's "top ten career performances to date, ordered
 * by number of victories". This port keeps that list locally, as the original
 * did, and it doubles as the input to the `[N] RANKING ACE!` end-of-career tier.
 */

import { aircraftLabel } from "../mission/mission-roster.ts";
import { REGION_LABELS } from "../mission/mission-world.ts";
import { awardName } from "./awards.ts";
import { formatCareerDate } from "./calendar.ts";
import {
  PILOT_RECORD_STATUS,
  currentAircraft,
  squadronOf,
  totalVictories,
  type CareerState,
} from "./career-state.ts";
import { rankOf } from "./service.ts";

export interface PilotRecord {
  readonly name: string;
  readonly rank: string;
  readonly status: string;
  readonly squadron: string;
  readonly aerodrome: string;
  readonly region: string;
  readonly planeType: string;
  readonly date: string;
  readonly score: number;
  readonly aircraft: number;
  readonly balloons: number;
  readonly zeppelins: number;
  readonly totalVictories: number;
  readonly unconfirmed: number;
  readonly medals: readonly string[];
  readonly missionsFlown: number;
  readonly wounds: number;
  readonly boardsOfInquiry: number;
}

export function pilotRecord(career: CareerState): PilotRecord {
  const unit = squadronOf(career);
  return {
    name: career.name,
    rank: rankOf(career.nation, career.rank).title,
    status: PILOT_RECORD_STATUS[career.status],
    squadron: unit.name,
    aerodrome: unit.aerodrome,
    region: REGION_LABELS[unit.region],
    planeType: aircraftLabel(currentAircraft(career)),
    date: formatCareerDate(career.date),
    score: career.careerScore,
    aircraft: career.confirmed.aircraft,
    balloons: career.confirmed.balloons,
    zeppelins: career.confirmed.zeppelins,
    totalVictories: totalVictories(career),
    unconfirmed: career.unconfirmed,
    medals: career.medals.map(awardName),
    missionsFlown: career.missionsFlown,
    wounds: career.wounds,
    boardsOfInquiry: career.strikes,
  };
}

/** The manual's own list length. */
export const TOP_ACES_LIMIT = 10;

export interface AceListing {
  readonly id: string;
  readonly name: string;
  readonly rank: string;
  readonly nation: CareerState["nation"];
  readonly victories: number;
  readonly score: number;
  readonly status: string;
}

export function aceListing(career: CareerState): AceListing {
  return {
    id: career.id,
    name: career.name,
    rank: rankOf(career.nation, career.rank).title,
    nation: career.nation,
    victories: totalVictories(career),
    score: career.careerScore,
    status: PILOT_RECORD_STATUS[career.status],
  };
}

/** Ordered by victories, then by score — the manual orders by victories alone. */
export function topAces(careers: readonly CareerState[], limit = TOP_ACES_LIMIT): readonly AceListing[] {
  return careers
    .map(aceListing)
    .sort((left, right) => right.victories - left.victories || right.score - left.score)
    .slice(0, limit);
}

/** This career's 1-based place on the top-aces list, or `undefined` if it missed it. */
export function topAceRanking(career: CareerState, careers: readonly CareerState[]): number | undefined {
  const index = topAces(careers).findIndex((entry) => entry.id === career.id);
  return index < 0 ? undefined : index + 1;
}
